import {
  ColumnSort,
  GroupingState,
  InitialTableState,
  PaginationState,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table';
import { useSessionStoragePagination } from '@va/util/hooks';
import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { TableSorting } from './DataTableV8';

export type ControlledTableState<T extends Record<string, any> | string = string> = {
  rowSelection: RowSelectionState;
  setPagination: (value: ((prevState: PaginationState) => PaginationState) | PaginationState) => void;
  pagination: { pageNumber: number; pageIndex: number; pageSize: number };
  setSorting: (value: ((prevState: ColumnSort[]) => ColumnSort[]) | ColumnSort[]) => void;
  setGrouping: (value: ((prevState: string[]) => string[]) | string[]) => void;
  sorting: ColumnSort[];
  setRowSelection: (value: ((prevState: RowSelectionState) => RowSelectionState) | RowSelectionState) => void;
  grouping: string[];
  defaultSorting?: TableSorting<T extends string ? T : Extract<keyof T, string> & string>[];
};

export const useControlledTableState = <T extends Record<string, any> | string = string>(
  initialState: InitialTableState & {
    defaultSorting?: TableSorting<T extends string ? T : Extract<keyof T, string> & string>[];
  } = {},
  sessionStoragePaginationKey: string,
): ControlledTableState<T> => {
  const { getStoredPageSize, storePageSize, getStoredPageIndex, storePageIndex } =
    useSessionStoragePagination(sessionStoragePaginationKey);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: getStoredPageIndex() ?? initialState.pagination?.pageIndex ?? 0,
    pageSize: getStoredPageSize() ?? initialState.pagination?.pageSize ?? 12,
  });

  const pageNumber = useMemo(() => pagination.pageIndex + 1, [pagination.pageIndex]);

  const [grouping, setGrouping] = useState<GroupingState>(initialState.grouping ?? []);
  const [sorting, setSorting] = useState<SortingState>(initialState.sorting ?? initialState.defaultSorting ?? []);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(initialState.rowSelection ?? {});

  const setPaginationMemoized = useCallback(
    (value: PaginationState | ((prevState: PaginationState) => PaginationState)) => {
      const val = typeof value === 'function' ? value(pagination) : value;

      if (isEqual(val, pagination)) {
        // Prevent unnecessary setPagination calls which in turn cause unnecessary re-renders
        return;
      }

      storePageIndex(val.pageIndex);
      storePageSize(val.pageSize);
      setPagination(val);
    },
    [pagination, storePageIndex, storePageSize],
  );

  const onSortingChange = useCallback<Dispatch<SetStateAction<SortingState>>>(
    (updater) =>
      setSorting((prev) => {
        const val = typeof updater === 'function' ? updater(prev) : updater;

        if (!val.length) return initialState.defaultSorting ?? [];

        return val;
      }),
    [initialState.defaultSorting],
  );

  return {
    pagination: { ...pagination, pageNumber },
    setPagination: setPaginationMemoized,
    grouping,
    setGrouping,
    sorting,
    setSorting: onSortingChange,
    rowSelection,
    setRowSelection,
    defaultSorting: initialState.defaultSorting,
  };
};
